import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import { useRecoilState, useRecoilValue } from 'recoil';
import localforage from 'localforage';
import { TABLE_CORRECTIONS } from '../../Services/TableService';
import DynamicTable from '../DynamicTable';
import Form from './Form';
import PermissionsService, { PERMISSION_TACHO_CORRECTION } from '../../Services/PermissionsService';
import { URL_CORRECTIONS } from '../../router/constants';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import { IDatepickerSettings, setTableDatepickerAtom } from '../../store/recoil/TableState';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import { selectedVehiclesAtom } from '../../store/recoil/vehicles';
import PageHeader from '../PageHeader';

export default function Corrections() {
  const { formatMessage: f } = useIntl();
  const [datepickerSettings, setDatepickerSettings] = useRecoilState<IDatepickerSettings | null>(
    setTableDatepickerAtom
  );
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [clickedVehicleId, setClickedVehicleId] = React.useState('');
  const [callTable, setCallTable] = React.useState<boolean>(false);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [locked, setLocked] = React.useState<string>('');
  const [data, setData] = React.useState<any>([]);
  const [pathname, navigate] = useLocation();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const query = useUrlQueryParams();
  const [, params] = useRoute(`${URL_CORRECTIONS}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle(f({ id: 'menu.corrections' }));

  const isCreatingAllowed = PermissionsService.hasAccess(
    PERMISSION_TACHO_CORRECTION,
    ['create'],
    selectedCustomers
  );

  const isEditingAllowed = PermissionsService.hasAccess(
    PERMISSION_TACHO_CORRECTION,
    ['update'],
    selectedCustomers
  );

  const buttons: any = {
    [TABLE_CORRECTIONS]: {
      handle: ['close'],
    },
  };

  if (isCreatingAllowed) {
    buttons[TABLE_CORRECTIONS].open = ['addBtn'];
  }
  if (isCreatingAllowed || isEditingAllowed) {
    buttons[TABLE_CORRECTIONS].handle.push('save');
  }

  const handleClose = React.useCallback(() => {
    navigate(URL_CORRECTIONS);
    setIsModalOpened(false);
    setData([]);
    setCustomerId(null);
    setLocked('');
  }, [navigate]);

  const updateTable = () => {
    setCallTable(true);
  };

  const handleFilter = React.useCallback(async () => {
    const vehicleId = await localforage.getItem('lastClickedVehicleId');
    if (vehicleId) {
      setClickedVehicleId(String(vehicleId));
      const date = new Date();
      const type = 'date_range_picker';
      setDatepickerSettings({
        ...datepickerSettings,
        timestamp: {
          type,
          date: [new Date(date.getFullYear(), date.getMonth(), 1), date],
          interval: 'THIS_MONTH',
        },
      });
      localforage.setItem('lastClickedVehicleId', '');
    }
  }, [datepickerSettings, setDatepickerSettings]);

  React.useEffect(() => {
    if (pathname === URL_CORRECTIONS) {
      setIsModalOpened(false);
      setCustomerId(null);
      setData([]);
      setDocumentTitle(f({ id: 'menu.corrections' }));
    } else if (pathname === `${URL_CORRECTIONS}/add`) {
      setIsModalOpened(true);
      const documentTitle = `${f({ id: 'table.add' })} - ${f({ id: 'menu.corrections' })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({ id: 'menu.corrections' })}`;
      setDocumentTitle(documentTitle);
      const customerIdUrl = query.get('customerId');
      const lockedFromUrl = query.get('locked');

      if (customerIdUrl) {
        setCustomerId(Number(customerIdUrl));
      }
      if (lockedFromUrl) {
        setLocked(lockedFromUrl);
      }
      setIsModalOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, pathname]);

  React.useEffect(() => {
    handleFilter();
  }, [handleFilter]);

  React.useEffect(() => {
    if (clickedVehicleId) {
      setClickedVehicleId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicles]);

  return (
    <>
      <PageHeader title={`${f({ id: 'menu.corrections' })}`} onlineHelpTopic="corrections" />
      <div className="col-12">
        <DynamicTable
          name={TABLE_CORRECTIONS}
          noTab={true}
          buttons={buttons[TABLE_CORRECTIONS]}
          mustClosed={false}
          updateTable={callTable}
          routerParams={params}
          filter={clickedVehicleId ? { licenceNumber: [clickedVehicleId] } : undefined}
        />
        {isModalOpened && !params && (
          <div>
            {PermissionsService.hasAccess(PERMISSION_TACHO_CORRECTION, ['read'], customerId) && (
              <Form
                id={null}
                customerId={customerId}
                updateTable={updateTable}
                handleClose={handleClose}
              />
            )}
          </div>
        )}
        {isModalOpened && params && (
          <div>
            {PermissionsService.hasAccess(
              PERMISSION_TACHO_CORRECTION,
              ['update', 'read'],
              customerId
            ) && (
              <Form
                id={Number(params.id)}
                customerId={customerId}
                // data={data}
                updateTable={updateTable}
                handleClose={handleClose}
                locked={locked}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
